'use client';

import React, { useState } from 'react';

// ─── BIOME DATA ───────────────────────────────────────────────────────────────
// 8 biomes matching the BIOME_RINGS in Plot3MuralEnhancements

const BIOME_DEEP_DIVE = [
  {
    id: 'grasslands',
    biome: 'Grasslands',
    arabic: 'الأراضي العشبية',
    icon: '🌾',
    color: '#8a8ab8',
    domain: 'Ships Sailing the Sea — Provision & Patience',
    verse: 'Quran 2:164',
    verseText: 'ships which sail in the sea with what benefits the people',
    names: [
      { name: 'As-Sabur', arabic: 'الصَّبُور', meaning: 'The Most Patient', observation: 'Observe how grasses endure drought, fire, and grazing — returning each season. Patience is not passive; it is the root system beneath the surface.' },
      { name: 'Al-Hayy', arabic: 'الْحَيُّ', meaning: 'The Ever-Living', observation: 'The grass dies back in winter and returns in spring. Life is not extinguished — it retreats to the root. What in your ward retreats but does not die?' },
      { name: 'Ar-Razzaq', arabic: 'الرَّزَّاق', meaning: 'The Provider', observation: 'Grasslands feed ungulates, insects, birds, and soil microbes. The provision chain is invisible until you trace it. Map the provision flows in your ward.' },
    ],
    sunnatAllahLayers: [
      { layer: 'Root Networks', desc: 'Grasses invest 80% of their biomass underground. The visible blade is a fraction of the plant. What is the underground infrastructure of your community?' },
      { layer: 'Fire Ecology', desc: 'Grasslands require periodic fire to remain healthy. Disturbance is not destruction — it is renewal. What disturbances in your ward are actually clearing space for new growth?' },
      { layer: 'Soil Carbon', desc: 'Prairies store more carbon than forests — underground, invisible, patient. Long-term investment in soil is the Sunnat of the grassland.' },
      { layer: 'Aquifer Recharge', desc: 'Deep grass roots channel rainwater into aquifers. The grassland gives water to the future. What does your ward give to the future?' },
    ],
    observationPrompts: [
      'Find a patch of grass — any grass. Sit with it for 3 minutes. What is it doing right now?',
      'Trace one provision chain in your ward: who feeds whom? Who provides what to whom?',
      'What in your community has endured repeated "fires" (crises, displacement, loss) and keeps returning?',
      'Where is the underground infrastructure of your ward — the roots that hold everything together but are rarely seen?',
      'Apply As-Sabur: What is the most patient thing in your ward? What has been waiting the longest?',
    ],
    irlLayers: [
      'Zahir: The visible blade — what you first see when you look at the grassland',
      'Sunnat Allah: The root system — the pattern of endurance and return beneath the surface',
      'Haqq: As-Sabur, Al-Hayy, Ar-Razzaq — the Names active in this biome',
      'Amanah: The grassland as trust — what does this biome ask of the caretaker?',
      'Uboor: The crossing — from observing grass to recognizing provision as a divine pattern',
    ],
  },
  {
    id: 'wetlands',
    biome: 'Wetlands',
    arabic: 'الأراضي الرطبة',
    icon: '🌿',
    color: '#2d8a87',
    domain: 'Rain Sent Down from the Sky — Purification & Filtration',
    verse: 'Quran 2:164',
    verseText: 'what Allah sent down from the sky of water, giving life to the earth after its death',
    names: [
      { name: 'Al-Latif', arabic: 'اللَّطِيف', meaning: 'The Subtle, The Gentle', observation: 'Wetlands filter toxins through subtle, invisible processes. The purification is not dramatic — it is patient, layered, gentle. Where in your ward does subtle purification happen?' },
      { name: 'Al-Khabir', arabic: 'الْخَبِير', meaning: 'The All-Aware', observation: 'The wetland knows what passes through it — every particle, every toxin, every seed. Al-Khabir is aware of what flows through your community.' },
      { name: 'Al-Muhyi', arabic: 'الْمُحْيِي', meaning: 'The Giver of Life', observation: 'Wetlands are among the most biodiverse ecosystems on earth. Life concentrates here. Where does life concentrate in your ward?' },
    ],
    sunnatAllahLayers: [
      { layer: 'Filtration', desc: 'Wetland plants and microbes remove nitrogen, phosphorus, and heavy metals from water. The purification is biological, not mechanical. What purifies your community?' },
      { layer: 'Flood Buffer', desc: 'Wetlands absorb floodwaters, protecting downstream communities. The wetland takes the hit so others don\'t have to. Who in your ward absorbs the impact for others?' },
      { layer: 'Nursery Function', desc: 'Many fish and birds breed in wetlands, then disperse. The wetland is a nursery for the wider ecosystem. What in your ward nurtures things that will go elsewhere?' },
      { layer: 'Still Water', desc: 'Wetlands are neither river nor lake — they are the in-between. The Barzakh of the water world. What is the in-between space in your community?' },
    ],
    observationPrompts: [
      'Find still water — a puddle, a pond, a drainage ditch. What lives in it or around it?',
      'What is being filtered in your ward right now — literally or metaphorically?',
      'Where is the "nursery" in your community — the place where new things begin before they go out into the world?',
      'Apply Al-Latif: What subtle, gentle process is happening in your ward that most people don\'t notice?',
      'Trace the water in your ward: where does it come from, where does it go, what does it pass through?',
    ],
    irlLayers: [
      'Zahir: The still surface — what you see when you look at the wetland',
      'Sunnat Allah: The filtration process — the pattern of purification beneath the surface',
      'Haqq: Al-Latif, Al-Khabir, Al-Muhyi — the Names active in this biome',
      'Amanah: The wetland as trust — what does this biome ask of the caretaker?',
      'Uboor: From observing still water to recognizing purification as a divine pattern',
    ],
  },
  {
    id: 'river',
    biome: 'River Systems',
    arabic: 'الأنهار',
    icon: '🌊',
    color: '#4a8ab8',
    domain: 'Every Moving Creature — Flow & Direction',
    verse: 'Quran 2:164',
    verseText: 'dispersing therein of every moving creature',
    names: [
      { name: 'Al-Muhyi', arabic: 'الْمُحْيِي', meaning: 'The Giver of Life', observation: 'Rivers carry life from mountains to sea — nutrients, seeds, organisms. The river is a provision highway. What are the provision highways in your ward?' },
      { name: 'Ar-Razzaq', arabic: 'الرَّزَّاق', meaning: 'The Provider', observation: 'Salmon carry ocean nutrients upstream into forests. The river connects distant ecosystems. What connects your ward to distant sources of provision?' },
      { name: 'Al-Wadud', arabic: 'الْوَدُود', meaning: 'The Most Loving', observation: 'Rivers shape the land through patient, persistent love — carving canyons over millennia. What in your ward is being shaped by patient, persistent care?' },
    ],
    sunnatAllahLayers: [
      { layer: 'Confluence', desc: 'Where two rivers meet, biodiversity spikes. Confluence is not collision — it is enrichment. Where do different streams of your community meet?' },
      { layer: 'Meander', desc: 'Rivers don\'t flow straight — they meander, finding the path of least resistance. The meander is not inefficiency; it is wisdom. What is the meandering path in your community work?' },
      { layer: 'Salmon Run', desc: 'Salmon return to their birth river to spawn and die, feeding the forest with their bodies. The return is the gift. What returns to your ward and feeds it?' },
      { layer: 'Riparian Zone', desc: 'The land along the river is the most productive — the edge between water and land. What are the productive edges in your ward?' },
    ],
    observationPrompts: [
      'Find flowing water — a stream, a drainage channel, even a gutter after rain. Follow it for 5 minutes.',
      'What is flowing through your ward right now — people, resources, information, money?',
      'Where are the confluences in your community — where different streams meet and enrich each other?',
      'Apply Al-Wadud: What in your ward is being shaped by patient, persistent love?',
      'Trace a provision highway: how does food, water, or money flow into and through your ward?',
    ],
    irlLayers: [
      'Zahir: The flowing surface — what you see when you look at the river',
      'Sunnat Allah: The current and confluence — the pattern of flow and direction',
      'Haqq: Al-Muhyi, Ar-Razzaq, Al-Wadud — the Names active in this biome',
      'Amanah: The river as trust — what does this biome ask of the caretaker?',
      'Uboor: From observing flow to recognizing provision as a divine pattern',
    ],
  },
  {
    id: 'forest',
    biome: 'Forest',
    arabic: 'الغابة',
    icon: '🌲',
    color: '#2d7a47',
    domain: 'Creation of the Earth — Structure & Depth',
    verse: 'Quran 2:164',
    verseText: 'creation of the heavens and the earth',
    names: [
      { name: 'Al-Khaliq', arabic: 'الْخَالِق', meaning: 'The Creator', observation: 'The forest creates its own climate — humidity, temperature, wind patterns. The creation creates conditions for further creation. What in your ward creates conditions for further flourishing?' },
      { name: 'Al-Bari', arabic: 'الْبَارِئ', meaning: 'The Originator', observation: 'Each species in the forest is an original design — no two trees are identical. Al-Bari\'s originality is expressed in infinite variation. What is original and unrepeatable in your ward?' },
      { name: 'Al-Musawwir', arabic: 'الْمُصَوِّر', meaning: 'The Fashioner of Forms', observation: 'The forest is a gallery of forms — leaf shapes, bark textures, root architectures. Al-Musawwir\'s artistry is everywhere. What forms in your ward reveal the Fashioner?' },
    ],
    sunnatAllahLayers: [
      { layer: 'Canopy to Root', desc: 'The forest has 8 layers — canopy, understory, shrub, herb, floor, root, mycorrhizal, bedrock. Each layer is a world. The IRL Lens has 8 layers for the same reason.' },
      { layer: 'Mycorrhizal Network', desc: 'Trees share nutrients through underground fungal networks — the "Wood Wide Web." The forest is a community, not a collection of individuals. What is the mycorrhizal network of your ward?' },
      { layer: 'Succession', desc: 'After disturbance, the forest rebuilds in stages — pioneer species, then shrubs, then trees. Succession is the Sunnat of recovery. What stage of succession is your ward in?' },
      { layer: 'Shade Tolerance', desc: 'Some trees grow in deep shade, waiting for a gap in the canopy. Patience in darkness is a forest strategy. What in your ward is growing in the shade, waiting for its moment?' },
    ],
    observationPrompts: [
      'Find a tree — any tree. Name it. Then apply the Three Questions: Is it? What is it? When is it?',
      'Look for the mycorrhizal network in your ward — the invisible connections that feed the community.',
      'What stage of succession is your ward in? What pioneer species are present?',
      'Apply Al-Khaliq: What in your ward creates conditions for further creation?',
      'Sit under a tree for 10 minutes. What does the canopy do to the light, the sound, the temperature?',
    ],
    irlLayers: [
      'Zahir: The canopy — what you see when you look at the forest',
      'Sunnat Allah: The mycorrhizal network — the pattern of connection beneath the surface',
      'Haqq: Al-Khaliq, Al-Bari, Al-Musawwir — the Names active in this biome',
      'Amanah: The forest as trust — what does this biome ask of the caretaker?',
      'Uboor: From observing a tree to recognizing creation as a divine pattern',
    ],
  },
  {
    id: 'mountain',
    biome: 'Mountain',
    arabic: 'الجبال',
    icon: '⛰️',
    color: '#7a6f5d',
    domain: 'Creation of the Earth — Stability & Witness',
    verse: 'Quran 78:7',
    verseText: 'And the mountains as stakes',
    names: [
      { name: 'Al-Mateen', arabic: 'الْمَتِين', meaning: 'The Firm, The Strong', observation: 'Mountains are the pegs of the earth — they stabilize the crust. Al-Mateen\'s firmness is expressed in stone. What is the mountain in your community — the stable, unmovable anchor?' },
      { name: 'Al-Aziz', arabic: 'الْعَزِيز', meaning: 'The Mighty', observation: 'Mountains are formed by immense forces — tectonic pressure over millions of years. The mighty is not sudden; it is the result of sustained pressure. What sustained pressure has shaped your ward?' },
      { name: 'Al-Hafiz', arabic: 'الْحَافِظ', meaning: 'The Preserver', observation: 'Mountains preserve ancient rock, fossils, glaciers — the archive of earth\'s history. Al-Hafiz preserves what would otherwise be lost. What is being preserved in your ward?' },
    ],
    sunnatAllahLayers: [
      { layer: 'Watershed Source', desc: 'Mountains are the source of rivers — snow and rain collect here and flow down to valleys. The mountain gives water to the lowlands. What in your ward is a source for others?' },
      { layer: 'Altitude Zones', desc: 'As you climb a mountain, you pass through multiple climate zones — each with its own ecology. The mountain contains multitudes. What are the altitude zones of your community?' },
      { layer: 'Glacial Memory', desc: 'Glaciers carve valleys, deposit boulders, shape landscapes — and then retreat, leaving their work behind. What has retreated from your ward but left its mark?' },
      { layer: 'Treeline', desc: 'Above the treeline, only the hardiest plants survive. The treeline is a threshold — a Barzakh between forest and alpine. What are the thresholds in your ward?' },
    ],
    observationPrompts: [
      'Find the highest point in your ward. Stand there. What do you see from the top?',
      'What is the mountain in your community — the stable, unmovable anchor that everything else is oriented around?',
      'Apply Al-Mateen: What in your ward is firm, strong, and reliable under pressure?',
      'What has been preserved in your ward that would otherwise be lost?',
      'Trace the watershed: where does the water in your ward come from? What mountain feeds it?',
    ],
    irlLayers: [
      'Zahir: The peak — what you see when you look at the mountain',
      'Sunnat Allah: The watershed — the pattern of source and flow from height to valley',
      'Haqq: Al-Mateen, Al-Aziz, Al-Hafiz — the Names active in this biome',
      'Amanah: The mountain as trust — what does this biome ask of the caretaker?',
      'Uboor: From observing stone to recognizing stability as a divine pattern',
    ],
  },
  {
    id: 'desert',
    biome: 'Desert',
    arabic: 'الصحراء',
    icon: '🏜️',
    color: '#c9a84c',
    domain: 'Clouds Controlled Between Sky and Earth — Endurance & Scarcity',
    verse: 'Quran 2:164',
    verseText: 'clouds controlled between the sky and the earth',
    names: [
      { name: 'As-Sabur', arabic: 'الصَّبُور', meaning: 'The Most Patient', observation: 'Desert plants wait years for rain, then bloom explosively. The desert bloom is the reward of patience. What in your ward is waiting for its moment to bloom?' },
      { name: 'Al-Ghani', arabic: 'الْغَنِيُّ', meaning: 'The Self-Sufficient', observation: 'The desert is self-sufficient — it needs nothing from outside to be what it is. Al-Ghani\'s self-sufficiency is expressed in the desert\'s completeness. What in your ward is complete in itself?' },
      { name: 'Al-Qadir', arabic: 'الْقَادِر', meaning: 'The All-Powerful', observation: 'The desert demonstrates that life persists even in the most extreme conditions. Al-Qadir\'s power is expressed in the survival of life against all odds. What survives in your ward against all odds?' },
    ],
    sunnatAllahLayers: [
      { layer: 'Scarcity as Teacher', desc: 'The desert teaches recognition of provision — when water is scarce, every drop is noticed. Scarcity is not punishment; it is a teacher of gratitude. What scarcity in your ward teaches gratitude?' },
      { layer: 'Desert Bloom', desc: 'After rare rains, the desert erupts in flowers — seeds that waited years for their moment. The bloom is proportional to the wait. What is waiting to bloom in your ward?' },
      { layer: 'Nocturnal Life', desc: 'Desert life is often nocturnal — active in the cool darkness, resting in the heat. The desert teaches timing. What in your ward operates in the cool of the night?' },
      { layer: 'Adaptation', desc: 'Desert organisms are masterpieces of adaptation — every feature is a response to constraint. Constraint produces creativity. What constraints in your ward have produced creative adaptations?' },
    ],
    observationPrompts: [
      'Find the driest, most barren spot in your ward. What lives there? What survives?',
      'Apply As-Sabur: What in your ward has been waiting the longest? What is it waiting for?',
      'What scarcity in your community has produced the most creative adaptations?',
      'Where is the desert bloom in your ward — the thing that erupts with life after a long wait?',
      'Apply Al-Ghani: What in your ward is complete in itself, needing nothing from outside?',
    ],
    irlLayers: [
      'Zahir: The bare surface — what you see when you look at the desert',
      'Sunnat Allah: The adaptation — the pattern of survival under constraint',
      'Haqq: As-Sabur, Al-Ghani, Al-Qadir — the Names active in this biome',
      'Amanah: The desert as trust — what does this biome ask of the caretaker?',
      'Uboor: From observing scarcity to recognizing endurance as a divine pattern',
    ],
  },
  {
    id: 'urban',
    biome: 'Urban',
    arabic: 'الحضري',
    icon: '🏙️',
    color: '#5a5a8a',
    domain: 'Every Moving Creature — Community & Khidmah',
    verse: 'Quran 49:13',
    verseText: 'We made you peoples and tribes that you may know one another',
    names: [
      { name: 'Al-Jami', arabic: 'الْجَامِع', meaning: 'The Gatherer', observation: 'The city gathers people from every background, language, and tradition. Al-Jami\'s gathering is expressed in urban diversity. What is being gathered in your ward right now?' },
      { name: 'Al-Adl', arabic: 'الْعَدْل', meaning: 'The Just', observation: 'The city reveals justice and injustice simultaneously — who has access, who doesn\'t. Al-Adl\'s justice is a standard against which the city is measured. Where is the gap between Al-Adl\'s standard and your ward\'s reality?' },
      { name: 'Al-Wali', arabic: 'الْوَلِيُّ', meaning: 'The Protecting Friend', observation: 'In the city, the question of who protects whom is always present. Al-Wali\'s protection is expressed in community solidarity. Who protects whom in your ward?' },
    ],
    sunnatAllahLayers: [
      { layer: 'Urban Ecology', desc: 'Cities have their own ecology — pigeons, rats, raccoons, coyotes, peregrine falcons. The urban biome is not separate from nature; it is a new expression of it. What wildlife has adapted to your ward?' },
      { layer: 'Infrastructure as Sunnat', desc: 'Water pipes, electrical grids, roads — the city\'s infrastructure is a human expression of the Sunnat of provision. When infrastructure fails, the Sunnat is revealed by its absence. What infrastructure in your ward is failing?' },
      { layer: 'Gentrification as Succession', desc: 'Gentrification follows the pattern of ecological succession — pioneer species (artists, young professionals) followed by climax species (luxury development). The community is the old-growth forest being cleared. What is being cleared in your ward?' },
      { layer: 'Masjid as Mycorrhizal Hub', desc: 'The masjid in an urban ward functions like a mycorrhizal hub — connecting, feeding, and sustaining the community network. Where is the mycorrhizal hub in your ward?' },
    ],
    observationPrompts: [
      'Walk one block in your ward. Count the number of different languages you hear or see.',
      'Apply Al-Adl: Where is the gap between justice and reality in your ward? Be specific.',
      'Find the urban wildlife in your ward — what has adapted to live alongside humans here?',
      'Where is the mycorrhizal hub in your ward — the place that connects and feeds the community network?',
      'Apply Al-Jami: What is being gathered in your ward right now? Who is doing the gathering?',
    ],
    irlLayers: [
      'Zahir: The street — what you see when you walk through the urban ward',
      'Sunnat Allah: The infrastructure and ecology — the pattern of provision and community',
      'Haqq: Al-Jami, Al-Adl, Al-Wali — the Names active in this biome',
      'Amanah: The urban ward as trust — what does this biome ask of the caretaker?',
      'Uboor: From observing the city to recognizing community as a divine pattern',
    ],
  },
  {
    id: 'sky',
    biome: 'Sky',
    arabic: 'السماء',
    icon: '☁️',
    color: '#6a8ab8',
    domain: 'Directing the Winds — Signs & Return',
    verse: 'Quran 51:21',
    verseText: 'And in yourselves — do you not see?',
    names: [
      { name: 'Al-Awwal', arabic: 'الْأَوَّل', meaning: 'The First', observation: 'The sky was before the earth — the heavens were created first. Al-Awwal\'s priority is expressed in the sky\'s primacy. What came first in your ward — before the buildings, before the people?' },
      { name: 'Al-Akhir', arabic: 'الْآخِر', meaning: 'The Last', observation: 'The sky will remain after the earth is gone. Al-Akhir\'s permanence is expressed in the sky\'s endurance. What in your ward will remain after everything else has changed?' },
      { name: 'Al-Zahir', arabic: 'الظَّاهِر', meaning: 'The Manifest', observation: 'The sky is the most visible thing — it covers everything, is seen by everyone, is always present. Al-Zahir\'s manifestation is expressed in the sky\'s omnipresence. What is the most visible thing in your ward?' },
    ],
    sunnatAllahLayers: [
      { layer: 'Weather as Sunnat', desc: 'Weather follows patterns — seasons, fronts, pressure systems. The Sunnat of the sky is expressed in meteorology. What are the weather patterns of your ward — literal and metaphorical?' },
      { layer: 'Light as Provision', desc: 'The sky delivers light — the foundation of all photosynthesis, all vision, all warmth. Light is the first provision. What is the light in your ward — the provision that makes everything else possible?' },
      { layer: 'Migration Routes', desc: 'Birds navigate by stars, magnetic fields, and landmarks. The sky is a highway for migration. What are the migration routes through your ward — who passes through, who stays?' },
      { layer: 'Barzakh of Sky', desc: 'The sky is the Barzakh between earth and the unseen — the threshold between the visible and the Ghayb. The sky is where the seen meets the unseen. Where is the Barzakh in your ward?' },
    ],
    observationPrompts: [
      'Look up. What do you see? Spend 5 minutes watching the sky.',
      'Apply Al-Zahir: What is the most manifest, most visible thing in your ward? What does everyone see?',
      'What are the migration routes through your ward — who passes through, who stays, who returns?',
      'Where is the Barzakh in your ward — the threshold between the visible and the unseen?',
      'Apply Al-Awwal: What came first in your ward? What is the oldest thing here?',
    ],
    irlLayers: [
      'Zahir: The visible sky — what you see when you look up',
      'Sunnat Allah: The weather and light — the pattern of provision from above',
      'Haqq: Al-Awwal, Al-Akhir, Al-Zahir — the Names active in this biome',
      'Amanah: The sky as trust — what does this biome ask of the caretaker?',
      'Uboor: From observing the sky to recognizing the Barzakh between seen and unseen',
    ],
  },
];

// ─── BIOME RING BUTTON ────────────────────────────────────────────────────────

function BiomeRingButton({ biome, isActive, onClick }: {
  biome: typeof BIOME_DEEP_DIVE[0];
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
        padding: '12px 8px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.25s',
        background: isActive ? `${biome.color}20` : 'rgba(255,255,255,0.02)',
        border: `2px solid ${isActive ? biome.color : biome.color + '30'}`,
        minWidth: 72, flex: '1 1 72px',
        transform: isActive ? 'scale(1.05)' : 'scale(1)',
        boxShadow: isActive ? `0 0 16px ${biome.color}30` : 'none',
      }}
    >
      <span style={{ fontSize: 22 }}>{biome.icon}</span>
      <span style={{ fontFamily: 'monospace', fontSize: 8, textTransform: 'uppercase', letterSpacing: '.08em', color: isActive ? biome.color : 'rgba(212,197,169,0.5)', textAlign: 'center', lineHeight: 1.3 }}>{biome.biome}</span>
      <span style={{ fontFamily: "'Amiri', serif", fontSize: 10, color: `${biome.color}60`, direction: 'rtl' }}>{biome.arabic}</span>
      {isActive && (
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: biome.color }} />
      )}
    </button>
  );
}

// ─── DEEP DIVE PANEL ─────────────────────────────────────────────────────────

function BiomeDeepDivePanel({ biome, onClose }: {
  biome: typeof BIOME_DEEP_DIVE[0];
  onClose: () => void;
}) {
  const [activeTab, setActiveTab] = useState<'names' | 'sunnat' | 'prompts' | 'irl'>('names');

  const tabs = [
    { id: 'names' as const, label: 'Names', arabic: 'الأسماء', icon: '✦' },
    { id: 'sunnat' as const, label: 'Sunnat Allah', arabic: 'سُنَّة', icon: '◎' },
    { id: 'prompts' as const, label: 'Observation Prompts', arabic: 'مُلَاحَظَة', icon: '⬡' },
    { id: 'irl' as const, label: 'IRL Layers', arabic: 'طَبَقَات', icon: '◈' },
  ];

  return (
    <div style={{
      background: 'rgba(6,13,18,0.98)',
      border: `1px solid ${biome.color}30`,
      borderLeft: `3px solid ${biome.color}`,
      borderRadius: 12,
      overflow: 'hidden',
      marginTop: 16,
    }}>
      {/* Panel header */}
      <div style={{ padding: '20px 24px 16px', background: `${biome.color}08`, borderBottom: `1px solid ${biome.color}15` }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 28 }}>{biome.icon}</span>
              <div>
                <h3 style={{ fontFamily: 'monospace', fontSize: 18, color: '#F5F0E8', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 2 }}>
                  {biome.biome}
                </h3>
                <span style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: `${biome.color}80` }}>{biome.arabic}</span>
              </div>
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 9, color: `${biome.color}70`, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>
              {biome.domain}
            </div>
            <div style={{ background: `${biome.color}10`, border: `1px solid ${biome.color}20`, borderRadius: 6, padding: '8px 12px', display: 'inline-block' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 8, color: `${biome.color}60`, letterSpacing: '.08em', textTransform: 'uppercase' }}>{biome.verse} — </span>
              <span style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, fontStyle: 'italic' }}>"{biome.verseText}"</span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: `1px solid rgba(255,255,255,0.1)`, borderRadius: 6, padding: '6px 10px', color: 'rgba(212,197,169,0.4)', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Tab navigation */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${biome.color}15`, overflowX: 'auto' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: '1 1 auto', padding: '12px 8px', cursor: 'pointer', transition: 'all 0.2s',
              background: activeTab === tab.id ? `${biome.color}12` : 'transparent',
              borderBottom: `2px solid ${activeTab === tab.id ? biome.color : 'transparent'}`,
              border: 'none', borderBottomWidth: 2, borderBottomStyle: 'solid',
              borderBottomColor: activeTab === tab.id ? biome.color : 'transparent',
            }}
          >
            <div style={{ fontFamily: 'monospace', fontSize: 8, textTransform: 'uppercase', letterSpacing: '.08em', color: activeTab === tab.id ? biome.color : 'rgba(212,197,169,0.35)', marginBottom: 2 }}>
              {tab.icon} {tab.label}
            </div>
            <div style={{ fontFamily: "'Amiri', serif", fontSize: 10, color: `${biome.color}50` }}>{tab.arabic}</div>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ padding: '20px 24px' }}>
        {/* Names tab */}
        {activeTab === 'names' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.35)', marginBottom: 4 }}>
              Names of Allah Active in the {biome.biome} Biome
            </p>
            {biome.names.map((name, i) => (
              <div key={i} style={{ background: `${biome.color}06`, border: `1px solid ${biome.color}20`, borderRadius: 10, padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 10 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${biome.color}15`, border: `1px solid ${biome.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: biome.color }}>{name.arabic.charAt(0)}</span>
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#F5F0E8', fontWeight: 700 }}>{name.name}</span>
                      <span style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: `${biome.color}80` }}>{name.arabic}</span>
                    </div>
                    <span style={{ fontFamily: 'monospace', fontSize: 9, color: `${biome.color}70`, letterSpacing: '.08em', textTransform: 'uppercase' }}>{name.meaning}</span>
                  </div>
                </div>
                <div style={{ borderTop: `1px solid ${biome.color}15`, paddingTop: 12 }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: `${biome.color}50`, marginBottom: 6 }}>Observation</p>
                  <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 12, lineHeight: 1.7 }}>{name.observation}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Sunnat Allah tab */}
        {activeTab === 'sunnat' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.35)', marginBottom: 4 }}>
              Sunnat Allah Layers — The Way of Allah in the {biome.biome}
            </p>
            {biome.sunnatAllahLayers.map((layer, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, padding: '14px 16px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${biome.color}15`, borderRadius: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: 6, background: `${biome.color}15`, border: `1px solid ${biome.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'monospace', fontSize: 10, color: biome.color, fontWeight: 700 }}>
                  {i + 1}
                </div>
                <div>
                  <p style={{ fontFamily: 'monospace', fontSize: 10, color: biome.color, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>{layer.layer}</p>
                  <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, lineHeight: 1.7 }}>{layer.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Observation Prompts tab */}
        {activeTab === 'prompts' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.35)', marginBottom: 4 }}>
              Field Observation Prompts — {biome.biome} Biome
            </p>
            {biome.observationPrompts.map((prompt, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, padding: '14px 16px', background: `${biome.color}06`, border: `1px solid ${biome.color}18`, borderRadius: 8 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: `${biome.color}20`, border: `1px solid ${biome.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'monospace', fontSize: 9, color: biome.color, fontWeight: 700 }}>
                  {i + 1}
                </div>
                <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 12, lineHeight: 1.7, fontStyle: 'italic' }}>{prompt}</p>
              </div>
            ))}
            <div style={{ marginTop: 8, padding: '12px 16px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }}>
              <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.3)', marginBottom: 6 }}>After your observation</p>
              <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.6 }}>
                Catalog what you recognized using the IRL Lens. Bridge your observation to the MRG — what resource, disparity, or context does this biome reveal in your ward?
              </p>
            </div>
          </div>
        )}

        {/* IRL Layers tab */}
        {activeTab === 'irl' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.35)', marginBottom: 4 }}>
              IRL Lens Layers — Applied to the {biome.biome} Biome
            </p>
            {biome.irlLayers.map((layer, i) => {
              const layerNames = ['Zahir', 'Sunnat Allah', 'Haqq', 'Amanah', 'Uboor'];
              const layerColors = [biome.color, '#c9a84c', '#8a8ab8', '#2d7a47', '#D4A82A'];
              const [layerLabel, ...rest] = layer.split(': ');
              return (
                <div key={i} style={{ display: 'flex', gap: 12, padding: '12px 14px', background: `${layerColors[i] || biome.color}06`, border: `1px solid ${layerColors[i] || biome.color}20`, borderRadius: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 6, background: `${layerColors[i] || biome.color}15`, border: `1px solid ${layerColors[i] || biome.color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'monospace', fontSize: 8, color: layerColors[i] || biome.color, fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}>
                    L{i + 1}
                  </div>
                  <div>
                    <p style={{ fontFamily: 'monospace', fontSize: 9, color: layerColors[i] || biome.color, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>{layerLabel}</p>
                    <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.6 }}>{rest.join(': ')}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN EXPORT ─────────────────────────────────────────────────────────────

export default function BiomeRingDeepDive({ compact = false }: { compact?: boolean }) {
  const [activeBiome, setActiveBiome] = useState<string | null>(null);

  const activeBiomeData = BIOME_DEEP_DIVE.find(b => b.id === activeBiome);

  return (
    <div>
      {/* Section header */}
      {!compact && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 8 }}>
            ◎ 8 Biome Rings — Click to Expand Deep-Dive
          </div>
          <h3 style={{ fontFamily: 'monospace', fontSize: 16, color: '#F5F0E8', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
            Biome Ring Explorer
          </h3>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.6, maxWidth: 600 }}>
            Each biome ring maps to a domain from Quran 2:164. Click any ring to expand Names of Allah, Sunnat Allah layers, and field observation prompts specific to that biome.
          </p>
        </div>
      )}

      {/* 8 biome ring buttons */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: activeBiome ? 0 : 8 }}>
        {BIOME_DEEP_DIVE.map(biome => (
          <BiomeRingButton
            key={biome.id}
            biome={biome}
            isActive={activeBiome === biome.id}
            onClick={() => setActiveBiome(activeBiome === biome.id ? null : biome.id)}
          />
        ))}
      </div>

      {/* Deep dive panel */}
      {activeBiomeData && (
        <BiomeDeepDivePanel
          biome={activeBiomeData}
          onClose={() => setActiveBiome(null)}
        />
      )}

      {!activeBiome && (
        <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.25)', letterSpacing: '.08em', marginTop: 8 }}>
          ↑ Click any biome ring to expand its deep-dive panel
        </p>
      )}
    </div>
  );
}

export { BIOME_DEEP_DIVE };
